import React, { useEffect, useState } from "react";
import api from "../../services/axios";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { jwtIsExpired } from "../../utils/jwtUtils";
import { homeRouteFor } from "../../utils/homeRoute";
import { consumirVoltar, guardarVoltar, voltarDaBusca } from "../../auth/voltar";
import { primePermissions } from "../../auth/PermissionProvider";
import { consumirAvisoDeLogin, saveSession } from "../../services/session";
import {
  MdMailOutline,
  MdLockOutline,
  MdVisibility,
  MdVisibilityOff,
  MdErrorOutline,
  MdArrowForward,
  MdAutoAwesome,
} from "react-icons/md";
import DataAiNewsPanel from "./components/DataAiNewsPanel";
import { exigeTrocaDeSenha, mensagemDeErroDeLogin } from "./loginError";
import {
  consumirLinkConvite,
  iniciarLogin,
  lerRetorno,
  limparRetorno,
  trocarPorIdToken,
} from "../../services/entra";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800 transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const SignIn: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState(() => consumirAvisoDeLogin());
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  // Mensagem vinda da troca de senha temporária ("Senha alterada. Entre com a nova senha.").
  const location = useLocation();
  const avisoDeSucesso = (location.state as { message?: string } | null)?.message;
  // Caminho que a pessoa abriu antes de cair aqui (já saneado: só `/admin/...`).
  const voltar = voltarDaBusca(location.search);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [entrando, setEntrando] = useState(false);

  function togglePasswordVisibility() {
    setIsPasswordVisible((prevState) => !prevState);
  }

  /**
   * A regra de destino vive em utils/homeRoute.ts — um lugar só. Ver o comentário de lá: esta
   * função já esteve duplicada no layout admin, e foi por isso que o portal acabou com três
   * "telas iniciais" diferentes.
   *
   * Espera as permissões antes de navegar: além de decidir o destino, deixa o cache quente, então
   * a primeira tela já vem com o menu completo em vez de montar item por item.
   */
  const homeFor = async (jwt: string, destino: string | null = voltar) => {
    const home = homeRouteFor(await primePermissions(jwt));
    return destino ?? home;
  };

  useEffect(() => {
    const jwtToken = localStorage.getItem("jwtToken");
    const logged = jwtIsExpired(jwtToken);

    if (!logged) {
      homeFor(jwtToken).then(navigate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Entrar com a Microsoft. A pessoa sai do portal, vai para a Microsoft e volta com `?code=` na
  // URL — então este efeito é a **segunda metade** do clique no botão, e roda no carregamento.
  useEffect(() => {
    const retorno = lerRetorno(window.location.search);
    if (retorno.tipo === "nenhum") return;

    // A Microsoft devolve a pessoa sem o `voltar` na URL; ele ficou guardado antes do redirect.
    const voltarDaMicrosoft = consumirVoltar();
    // Convite Microsoft: o token do link vale para esta volta só, haja sucesso ou erro.
    const linkToken = consumirLinkConvite();

    if (retorno.tipo === "erro") {
      limparRetorno();
      setErrorMessage(retorno.mensagem);
      return;
    }

    (async () => {
      setEntrando(true);
      try {
        const idToken = await trocarPorIdToken(retorno.codigo, retorno.verifier);
        limparRetorno();

        const resposta = await api.post("/Auth/Entra", linkToken ? { idToken, linkToken } : { idToken });
        saveSession(resposta.data.jwtToken, resposta.data.refreshToken);
        localStorage.setItem("userEmail", resposta.data.email);

        navigate(await homeFor(resposta.data.jwtToken, voltarDaMicrosoft));
        toast.success("👋 Você está logado. Bem-vindo!", { position: "top-right", autoClose: 5000 });
      } catch (erro: any) {
        limparRetorno();
        // A mensagem do servidor é a que ajuda ("Seu acesso ainda não foi liberado"), e por isso ela
        // vence a genérica. É a contrapartida da decisão do backend de detalhar as recusas aqui.
        setErrorMessage(
          erro?.response?.data?.message ||
            erro?.message ||
            "Não foi possível entrar com a conta da Microsoft."
        );
      } finally {
        setEntrando(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEntraLogin = async () => {
    setErrorMessage("");
    setEntrando(true);
    guardarVoltar(voltar);
    try {
      await iniciarLogin(); // sai da página
    } catch {
      setEntrando(false);
      setErrorMessage("Não foi possível abrir o login da Microsoft.");
    }
  };

  const handleLogin = async () => {
    if (email !== null && password !== null) {
      try {
        setLoading(true);
        setErrorMessage("");
        const response = await api.post("/Auth/Login", { email, password });
        // Guarda o par: o acesso dura 30 min, e é o refresh que evita a sessão cair no meio do
        // trabalho — o interceptor do axios renova sozinho quando o acesso vence.
        saveSession(response.data.jwtToken, response.data.refreshToken);
        localStorage.setItem("userEmail", response.data.email);

        navigate(await homeFor(response.data.jwtToken));
        toast.success("👋 Você está logado. Bem-vindo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } catch (error) {
        // Senha temporária: a senha está certa, falta trocá-la. Vai para a tela de troca levando
        // e-mail e a senha digitada no state de navegação — nada em storage.
        if (exigeTrocaDeSenha(error)) {
          setLoading(false);
          navigate("/auth/change-temporary-password", {
            state: { email, temporaryPassword: password },
          });
          return;
        }
        // A recusa por colaborador desligado vem com texto próprio do servidor; a frase fixa
        // daqui dizia "verifique sua senha" e mandava a pessoa caçar um problema que não existe.
        setErrorMessage(mensagemDeErroDeLogin(error));
        setLoading(false);
        toast.error("😞 Ops! Algo deu errado.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    }
  };

  const isDisabled = email.length < 5 || password.length < 5;

  return (
    <div className="flex min-h-dvh w-full bg-white dark:bg-[#070710]">
      {/* ── Painel esquerdo: formulário ───────────────────────────────────── */}
      <div className="relative flex w-full flex-col justify-between px-6 py-8 sm:px-10 lg:w-[70%] lg:px-12 xl:px-24">
        {/* Fundo: light = glows suaves de marca; dark = cúpula de luz (hero Vertex). */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-brand-500/10 blur-3xl dark:hidden" />
          <div className="absolute bottom-10 right-0 h-64 w-64 rounded-full bg-blueSecondary/10 blur-3xl dark:hidden" />
          <div className="nina-hero-dome absolute inset-x-0 -bottom-[10%] hidden h-[72%] dark:block" />
        </div>

        {/* Topo: logo em alta qualidade (SVG vetorial) */}
        <header className="relative z-10">
          <img
            src="/datavalue-logo.svg"
            alt="DataValue"
            className="h-8 w-auto dark:brightness-0 dark:invert"
          />
        </header>

        {/* Centro: formulário */}
        <main className="animate-fade-up relative z-10 mx-auto w-full max-w-sm py-10">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-dv-blue-500/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-dv-blue-600 dark:text-dv-cyan-300">
            <MdAutoAwesome className="h-3.5 w-3.5" aria-hidden="true" />
            Portal DataValue
          </span>
          <h1 className="mt-4 bg-[linear-gradient(180deg,#0b1437_0%,rgba(11,20,55,0.62)_100%)] bg-clip-text text-3xl font-extrabold tracking-tight text-transparent dark:bg-[linear-gradient(180deg,#FFF_0%,rgba(255,255,255,0.55)_100%)]">
            Bem-vindo de volta
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            Entre com suas credenciais para acessar sua área de trabalho.
          </p>

          <form onSubmit={(e) => e.preventDefault()} className="mt-8">
            {avisoDeSucesso && !errorMessage && (
              <div
                role="status"
                className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-900/20 dark:text-emerald-300"
              >
                {avisoDeSucesso}
              </div>
            )}
            {errorMessage && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
              >
                <MdErrorOutline className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="email"
                className="text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                E-mail
              </label>
              <div className="relative">
                <MdMailOutline
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !isDisabled && handleLogin()}
                  className={`${inputCls} pl-9`}
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div className="mt-4 flex flex-col gap-1.5">
              <label
                htmlFor="password"
                className="text-sm font-semibold text-gray-700 dark:text-gray-300"
              >
                Senha
              </label>
              <div className="relative">
                <MdLockOutline
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !isDisabled && handleLogin()}
                  className={`${inputCls} px-9`}
                  placeholder="Digite sua senha"
                  type={isPasswordVisible ? "text" : "password"}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  aria-label={isPasswordVisible ? "Ocultar senha" : "Mostrar senha"}
                  aria-pressed={isPasswordVisible}
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-brand-600 dark:hover:bg-navy-700"
                >
                  {isPasswordVisible ? (
                    <MdVisibilityOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <MdVisibility className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-2 text-right">
              <Link
                to="/auth/esqueci"
                className="rounded-lg text-sm font-semibold text-dv-blue-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-dv-blue-500 dark:text-dv-cyan-300 dark:focus-visible:ring-dv-cyan-300"
              >
                Esqueci minha senha
              </Link>
            </div>

            {/* Submit — CTA sólido estilo Vertex (branco no dark / navy no light) */}
            <button
              type="submit"
              onClick={handleLogin}
              disabled={loading || isDisabled}
              className="group mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-navy-800 px-5 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-navy-900 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-navy-900 dark:shadow-lg dark:shadow-white/10 dark:hover:bg-gray-100"
            >
              {loading ? (
                <>
                  <span
                    className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70"
                    aria-hidden="true"
                  />
                  Processando…
                </>
              ) : (
                <>
                  Entrar
                  <MdArrowForward className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </button>

            {/* Entrar com a Microsoft — o caminho que vai virar o único (SPEC 007) */}
            <div className="mt-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
              <span className="text-xs font-medium text-gray-400 dark:text-white/50">ou</span>
              <span className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
            </div>

            <button
              type="button"
              onClick={handleEntraLogin}
              disabled={entrando}
              className="group mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/15 dark:bg-transparent dark:text-gray-200 dark:hover:bg-white/5"
            >
              {entrando ? (
                <>
                  <span
                    className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70"
                    aria-hidden="true"
                  />
                  Conectando…
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
                    <path fill="#f35325" d="M1 1h10v10H1z" />
                    <path fill="#81bc06" d="M12 1h10v10H12z" />
                    <path fill="#05a6f0" d="M1 12h10v10H1z" />
                    <path fill="#ffba08" d="M12 12h10v10H12z" />
                  </svg>
                  Entrar com a Microsoft
                </>
              )}
            </button>

            {/* CTA secundário fantasma (estilo Vertex) */}
            <a
              href="mailto:suporte@datavalue.com.br?subject=Ajuda%20para%20acessar%20o%20Portal%20NINA"
              className="group mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
            >
              Precisa de ajuda?
              <MdArrowForward className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </form>
        </main>

        {/* Rodapé */}
        <footer className="relative z-10 text-center text-xs text-gray-400 dark:text-white/60">
          © {new Date().getFullYear()} DataValue · Todos os direitos reservados
        </footer>
      </div>

      {/* ── Painel direito: Breaking News (Dados & IA) ────────────────────── */}
      <aside className="hidden lg:block lg:w-[30%]">
        <DataAiNewsPanel />
      </aside>
    </div>
  );
};

export default SignIn;
